import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

import {
  partsOf, fromParts, daysInMonth, toIsoDay, today, monthNames, formatDate, normalizeDateSystem,
} from '../../tools/calendars';

// A date field for a MIS document, in the document's own calendar.
//
// The VALUE is always a Gregorian 'YYYY-MM-DD' string - what the API stores and what sorting and
// filtering work on. The `system` only decides how it is entered and shown:
//   gregorian  the browser's own date input
//   jalali     year · month name · day   (the Iranian order, as 1405/07/25 is written)
//   hijri      day · month name · year
// so switching a document's calendar re-draws the SAME date; it never moves it.
//
// Jalali and Hijri have no native input, so they are three native fields. That is also what keeps
// them usable from the keyboard: each is walked with Tab, Enter or the arrow keys (see
// tools/enterAdvancesFields.js), and a day or month is picked by typing its first characters.

const fieldSx = (sx) => ({ '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' }, ...sx });
const labelProps = { shrink: true, style: { fontSize: '0.75rem' } };
const inputProps = { style: { fontSize: '0.8rem' } };

function CalendarParts({ label, value, onChange, system, error, helperText, disabled, clearable, onBlur, sx }) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'en').slice(0, 2);

  const current = useMemo(() => (value ? partsOf(new Date(value), system) : null), [value, system]);
  // The year is typed, so it keeps its own text until it is a whole four-digit year.
  const [yearText, setYearText] = useState(current ? String(current.year) : '');
  useEffect(() => { setYearText(current ? String(current.year) : ''); }, [current && current.year]); // eslint-disable-line react-hooks/exhaustive-deps

  const months = useMemo(() => monthNames(system, lang), [system, lang]);
  // Whatever the person has not set yet starts from today in this calendar.
  const base = current || partsOf(today(), system);
  const dim = daysInMonth(base.year, base.month, system);

  const emit = (p) => { const d = fromParts(p, system); if (d) onChange(toIsoDay(d)); };
  const setDay = (day) => emit({ ...base, day: Number(day) });
  const setMonth = (month) => {
    const m = Number(month);
    emit({ ...base, month: m, day: Math.min(base.day, daysInMonth(base.year, m, system)) });
  };
  const setYear = (text) => {
    const clean = text.replace(/\D/g, '').slice(0, 4);
    setYearText(clean);
    if (clean.length === 4) {
      const y = Number(clean);
      emit({ ...base, year: y, day: Math.min(base.day, daysInMonth(y, base.month, system)) });
    }
  };

  const dayField = (
    <TextField key="day" select size="small" label={t('mis.dateDay')} value={current ? current.day : ''}
      onChange={(e) => setDay(e.target.value)} onBlur={onBlur} disabled={disabled} error={Boolean(error)}
      SelectProps={{ native: true }} InputLabelProps={labelProps} inputProps={inputProps} sx={fieldSx(sx)}>
      {!current && <option value="" />}
      {Array.from({ length: dim }, (_, i) => <option key={i + 1} value={i + 1}>{i + 1}</option>)}
    </TextField>
  );
  const monthField = (
    <TextField key="month" select size="small" label={t('mis.dateMonth')} value={current ? current.month : ''}
      onChange={(e) => setMonth(e.target.value)} onBlur={onBlur} disabled={disabled} error={Boolean(error)}
      SelectProps={{ native: true }} InputLabelProps={labelProps} inputProps={inputProps} sx={fieldSx(sx)}>
      {!current && <option value="" />}
      {months.map((name, i) => <option key={i + 1} value={i + 1}>{`${i + 1} · ${name}`}</option>)}
    </TextField>
  );
  const yearField = (
    <TextField key="year" size="small" label={t('mis.dateYear')} value={yearText}
      onChange={(e) => setYear(e.target.value)} onBlur={onBlur} disabled={disabled} error={Boolean(error)}
      InputLabelProps={labelProps} inputProps={{ ...inputProps, inputMode: 'numeric', maxLength: 4, dir: 'ltr' }} sx={fieldSx(sx)} />
  );

  const fields = system === 'jalali' ? [yearField, monthField, dayField] : [dayField, monthField, yearField];
  const columns = system === 'jalali' ? '1fr 1.9fr 0.9fr' : '0.9fr 1.9fr 1fr';

  return (
    <Box role="group" aria-label={label}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, minHeight: 18 }}>
        <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', flexGrow: 1 }}>{label}</Typography>
        {clearable && value && !disabled && (
          <IconButton size="small" onClick={() => onChange('')} aria-label={t('common.clear')} sx={{ p: 0.25 }}>
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        )}
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: columns, gap: 0.75 }}>{fields}</Box>
      {(error || helperText) && (
        <Typography sx={{ fontSize: '0.68rem', mt: 0.5, color: error ? 'error.main' : 'text.secondary' }}>
          {error || helperText}
        </Typography>
      )}
      {current && (
        <Typography sx={{ fontSize: '0.68rem', mt: 0.5, color: 'text.secondary' }}>
          {t('mis.dateAsGregorian', { date: formatDate(new Date(value), 'gregorian') })}
        </Typography>
      )}
    </Box>
  );
}

/**
 * @param value   Gregorian 'YYYY-MM-DD' (or '')
 * @param onChange(iso)  called with a Gregorian 'YYYY-MM-DD', or '' when cleared
 * @param system  'gregorian' | 'jalali' | 'hijri'
 */
export default function MisDateField({
  label, value, onChange, system, error, helperText, disabled, clearable, onBlur, sx,
}) {
  const sys = normalizeDateSystem(system);
  if (sys === 'gregorian') {
    return (
      <TextField size="small" label={label} type="date" fullWidth value={value || ''}
        onChange={(e) => onChange(e.target.value)} onBlur={onBlur} disabled={disabled}
        error={Boolean(error)} helperText={error || helperText}
        InputLabelProps={labelProps} inputProps={inputProps} sx={fieldSx(sx)} />
    );
  }
  return (
    <CalendarParts label={label} value={value} onChange={onChange} system={sys} error={error}
      helperText={helperText} disabled={disabled} clearable={clearable} onBlur={onBlur} sx={sx} />
  );
}

export { showDocDate } from './docFormat';
